import api from './api';

// Task service for API communication
const taskService = {
  // Get all tasks with optional filters
  getTasks: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      // Handle status parameter - can be string or array
      if (filters.status) {
        if (Array.isArray(filters.status)) {
          // For multiple status values, use a different approach
          // Send as status[] for Laravel to handle as array
          filters.status.forEach(status => {
            params.append('status[]', status);
          });
        } else {
          params.append('status', filters.status);
        }
      }

      if (filters.priority) params.append('priority', filters.priority);
      if (filters.project_code) params.append('project_code', filters.project_code);
      if (filters.agent) params.append('agent', filters.agent);
      if (filters.order_by) params.append('order_by', filters.order_by);
      // Convert boolean to 1/0 for Laravel's boolean validation
      if (filters.include_disabled !== undefined) {
        params.append('include_disabled', filters.include_disabled ? 1 : 0);
      }

      const queryString = params.toString();
      const url = queryString ? `/api/tasks?${queryString}` : '/api/tasks';

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get a specific task
  getTask: async (id) => {
    try {
      const response = await api.get(`/api/tasks/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create a new task
  createTask: async (taskData) => {
    try {
      const response = await api.post('/api/tasks', taskData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update an existing task
  updateTask: async (id, taskData) => {
    try {
      const response = await api.put(`/api/tasks/${id}`, taskData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete a task
  deleteTask: async (id) => {
    try {
      const response = await api.delete(`/api/tasks/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get status options
  getStatusOptions: async () => {
    try {
      const response = await api.get('/api/tasks/status/options');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get priority options
  getPriorityOptions: async () => {
    try {
      const response = await api.get('/api/tasks/priority/options');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Advance task to next status
  advanceTaskStatus: async (id) => {
    try {
      const response = await api.put(`/api/tasks/${id}/advance-status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get responses for a task
  getTaskResponses: async (taskId) => {
    try {
      const response = await api.get(`/api/tasks/${taskId}/responses`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create a response for a task
  createTaskResponse: async (taskId, responseData) => {
    try {
      const response = await api.post(`/api/tasks/${taskId}/responses`, responseData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update a response
  updateTaskResponse: async (taskId, responseId, responseData) => {
    try {
      const response = await api.put(`/api/tasks/${taskId}/responses/${responseId}`, responseData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete a response
  deleteTaskResponse: async (taskId, responseId) => {
    try {
      const response = await api.delete(`/api/tasks/${taskId}/responses/${responseId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get relations for a task
  getTaskRelations: async (taskId) => {
    try {
      const response = await api.get(`/api/tasks/${taskId}/relations`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create a relation for a task
  createTaskRelation: async (taskId, relationData) => {
    try {
      const response = await api.post(`/api/tasks/${taskId}/relations`, relationData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete a relation
  deleteTaskRelation: async (taskId, relationId) => {
    try {
      const response = await api.delete(`/api/tasks/${taskId}/relations/${relationId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Task status constants
export const TASK_STATUS = {
  CREATED: 'created',
  PENDING: 'pending',
  WORKING: 'working',
  REVIEWING: 'reviewing',
  FINISHED: 'finished',
  FAILED: 'failed',
  REJECTED: 'rechazada'
};

// Task priority constants
export const TASK_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

// Status labels for display
export const STATUS_LABELS = {
  [TASK_STATUS.CREATED]: 'Creada',
  [TASK_STATUS.PENDING]: 'Pendiente',
  [TASK_STATUS.WORKING]: 'En Curso',
  [TASK_STATUS.REVIEWING]: 'En Revisión',
  [TASK_STATUS.FINISHED]: 'Finalizada',
  [TASK_STATUS.FAILED]: 'Fallida',
  [TASK_STATUS.REJECTED]: 'Rechazada'
};

// Priority labels for display
export const PRIORITY_LABELS = {
  [TASK_PRIORITY.LOW]: 'Baja',
  [TASK_PRIORITY.MEDIUM]: 'Media',
  [TASK_PRIORITY.HIGH]: 'Alta',
  [TASK_PRIORITY.URGENT]: 'Urgente'
};

// Status colors for UI
export const STATUS_COLORS = {
  [TASK_STATUS.CREATED]: 'bg-indigo-500',
  [TASK_STATUS.PENDING]: 'bg-orange-500',
  [TASK_STATUS.WORKING]: 'bg-lightBlue-500',
  [TASK_STATUS.REVIEWING]: 'bg-yellow-500',
  [TASK_STATUS.FINISHED]: 'bg-emerald-500',
  [TASK_STATUS.FAILED]: 'bg-red-500',
  [TASK_STATUS.REJECTED]: 'bg-pink-700'
};

// Status background inline styles (avoids Tailwind purge on dynamic classes)
export const STATUS_BG_STYLES = {
  [TASK_STATUS.CREATED]:  { backgroundColor: '#6366f1' },
  [TASK_STATUS.PENDING]:  { backgroundColor: '#f97316' },
  [TASK_STATUS.WORKING]:  { backgroundColor: '#0ea5e9' },
  [TASK_STATUS.REVIEWING]:{ backgroundColor: '#eab308' },
  [TASK_STATUS.FINISHED]: { backgroundColor: '#10b981' },
  [TASK_STATUS.FAILED]:   { backgroundColor: '#ef4444' },
  [TASK_STATUS.REJECTED]: { backgroundColor: '#be185d' },
};

// Priority colors for UI
export const PRIORITY_COLORS = {
  [TASK_PRIORITY.LOW]: 'bg-lightBlue-500',
  [TASK_PRIORITY.MEDIUM]: 'bg-yellow-500',
  [TASK_PRIORITY.HIGH]: 'bg-orange-500',
  [TASK_PRIORITY.URGENT]: 'bg-red-500'
};

// Priority background inline styles (avoids Tailwind purge on dynamic classes)
export const PRIORITY_BG_STYLES = {
  [TASK_PRIORITY.LOW]:    { backgroundColor: '#0ea5e9' },
  [TASK_PRIORITY.MEDIUM]: { backgroundColor: '#eab308' },
  [TASK_PRIORITY.HIGH]:   { backgroundColor: '#f97316' },
  [TASK_PRIORITY.URGENT]: { backgroundColor: '#ef4444' },
};

// Status workflow mapping
export const STATUS_WORKFLOW = {
  [TASK_STATUS.CREATED]: TASK_STATUS.PENDING,
  [TASK_STATUS.PENDING]: TASK_STATUS.WORKING,
  [TASK_STATUS.WORKING]: TASK_STATUS.REVIEWING,
  [TASK_STATUS.REVIEWING]: TASK_STATUS.FINISHED,
  [TASK_STATUS.FINISHED]: null,  // No next status - task complete
  [TASK_STATUS.FAILED]: null,    // No next status - task failed
  [TASK_STATUS.REJECTED]: null   // No next status - task rejected
};

// Helper functions
export const getNextStatus = (currentStatus) => {
  return STATUS_WORKFLOW[currentStatus] || null;
};

export const getNextStatusLabel = (currentStatus) => {
  const nextStatus = getNextStatus(currentStatus);
  return nextStatus ? STATUS_LABELS[nextStatus] : null;
};

export const getNextStatusAction = (currentStatus) => {
  const actions = {
    [TASK_STATUS.CREATED]: 'Planificar',
    [TASK_STATUS.PENDING]: 'Iniciar',
    [TASK_STATUS.WORKING]: 'Revisar',
    [TASK_STATUS.REVIEWING]: 'Finalizar'
  };
  return actions[currentStatus] || null;
};

export default taskService;